import DataTable from "Components/UI/DataTable";
import { ColDef } from "ag-grid-community";
import { useMemo } from "react";
import { IAdStat } from "features/attributionAnalytics/types";
import GlassCard from "Components/GlassCard/GlassCard";
import { ApiAttributionModel, ApiMetric } from "@incendium/api";
import {
  AttributionMetric,
  metricByAttribution,
  useFormatMetric,
} from "features/analytics";
import { Stack, Typography } from "@mui/material";
import CampaignCellIcon from "./CampaignCellIon";
import { percentageChange } from "Helpers/percentage";
import { useAttributionContext } from "Providers/AttributionProvider";

interface IAdStatsTableProps {
  ads: IAdStat[];
  publisherName: string;
}

function AdStatsTable({ ads, publisherName }: IAdStatsTableProps) {
  const formatMetric = useFormatMetric();
  const { model } = useAttributionContext();
  const resolvedRoasMetric = metricByAttribution(
    model || ApiAttributionModel.EBA,
    AttributionMetric.ROAS,
  ) as ApiMetric;
  const resolvedSalesCountMetric = metricByAttribution(
    model || ApiAttributionModel.EBA,
    AttributionMetric.SALES,
  ) as ApiMetric;

  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: "Ad Name",
        field: "name",
        pinned: "left",
        width: 320,
        cellRenderer: ({ value, data }) => {
          const imageUrl = data?.imageUrl;
          return (
            <Stack
              sx={{
                transform: "translateX(-8px)",
                justifyContent: "flex-start!important",
                height: 44,
              }}
              direction={"row"}
              spacing={1}
              alignItems="center"
            >
              <CampaignCellIcon imageUrl={imageUrl || ""} size={30} rounded />
              <Typography noWrap>{value}</Typography>
            </Stack>
          );
        },
      },
      {
        headerName: `${publisherName} Reported`,
        children: [
          {
            headerName: "Spend",
            field: "spend",
            minWidth: 120,
            textAlign: "center",
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_SPEND, value),
          },
          {
            headerName: "Clicks",
            field: "clicks",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_PUBLISHER_CLICKS, value),
          },
          {
            headerName: "CPC",
            field: "cpc",
            cellStyle: {
              textAlign: "center",
            },
            minWidth: 100,
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_PUBLISHER_CPC, value),
          },
          {
            headerName: "Publisher ROAS",
            field: "publisherRoas",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_PUBLISHER_ROAS, value),
          },
        ],
      },
      {
        headerName: "Tracked by us",
        children: [
          {
            headerName: "Sessions",
            field: "sessions",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_SESSIONS_COUNT, value),
          },
          {
            headerName: "Cart Rate",
            field: "cartRate",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(
                ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                value,
              ),
          },
          {
            headerName: "CR",
            field: "cr",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_MACRO_CONVERSION_RATE, value),
          },
          {
            headerName: "ROAS",
            field: "roas",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_ROAS, value),
          },
        ],
      },
      {
        headerName: "Variance",
        children: [
          {
            headerName: "Clicks",
            field: "varianceClicks",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(ApiMetric.METRIC_PUBLISHER_CLICKS_DIFFERENCE, value),
          },
          {
            headerName: "Sales",
            field: "varianceSales",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(resolvedSalesCountMetric, value),
          },
          {
            headerName: "ROAS",
            field: "varianceRoas",
            minWidth: 120,
            cellStyle: {
              textAlign: "center",
            },
            valueFormatter: ({ value }) =>
              formatMetric(resolvedRoasMetric, value),
          },
        ],
      },
    ],
    [publisherName, formatMetric, resolvedRoasMetric, resolvedSalesCountMetric],
  );

  const rowData = useMemo(() => {
    return ads.map((ad) => {
      const variance = {
        clicks: percentageChange(
          Number(ad.data[ApiMetric.METRIC_PUBLISHER_CLICKS] || 0),
          Number(ad.data[ApiMetric.METRIC_SESSIONS_COUNT] || 0),
        ),
        sales: percentageChange(
          Number(ad.data[ApiMetric.METRIC_PUBLISHER_SALES_COUNT] || 0),
          Number(ad.data[resolvedSalesCountMetric] || 0),
        ),
        roas: percentageChange(
          Number(ad.data[ApiMetric.METRIC_PUBLISHER_ROAS] || 0),
          Number(ad.data[resolvedRoasMetric] || 0),
        ),
      };

      return {
        name: ad.name,
        imageUrl: ad.imageUrl,
        spend: ad.data[ApiMetric.METRIC_SPEND],
        cpc: ad.data[ApiMetric.METRIC_PUBLISHER_CPC] || 0,
        publisherRoas: ad.data[ApiMetric.METRIC_PUBLISHER_ROAS] || 0,
        clicks: ad.data[ApiMetric.METRIC_PUBLISHER_CLICKS] || 0,
        sessions: ad.data[ApiMetric.METRIC_SESSIONS_COUNT] || 0,
        cartRate: ad.data[ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE] || 0,
        cr: ad.data[ApiMetric.METRIC_MACRO_CONVERSION_RATE] || 0,
        roas: ad.data[ApiMetric.METRIC_ROAS] || 0,
        varianceClicks: variance.clicks,
        varianceSales: variance.sales,
        varianceRoas: variance.roas,
      };
    });
  }, [ads, resolvedRoasMetric, resolvedSalesCountMetric]);

  return (
    <GlassCard sx={{ height: "100%" }} boxProps={{ p: 0 }}>
      <DataTable colDefs={columns} rows={rowData || []} pageSize={0} />
    </GlassCard>
  );
}

export default AdStatsTable;
